import React, { useState, useCallback, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Collapse,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Tooltip,
  Divider,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Send as SendIcon,
  Warning as WarningIcon,
  Email as EmailIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  WhatsApp as WhatsAppIcon,
  LocalPostOffice as PostaIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { invoicesApi, aziendeApi } from '../services/api';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { 
    style: 'currency', 
    currency: 'EUR' 
  }).format(value || 0);
};

const formatDate = (date) => {
  if (!date) return '-';
  try {
    return format(new Date(date), 'dd/MM/yyyy', { locale: it });
  } catch {
    return '-';
  }
};

const formatDateTime = (date) => {
  if (!date) return '-';
  try {
    return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: it });
  } catch {
    return '-';
  }
};

const getDaysFromNow = (date) => {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  return Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
};

const getChannelLabel = (channel) => {
  if (channel === 'whatsapp') return 'WhatsApp';
  if (channel === 'posta') return 'Posta';
  return 'Email';
};

// Componente riga espandibile con preview email
const ExpandableRow = ({ preview, selected, onSelect, type, isMobile, channel = 'email' }) => {
  const [expanded, setExpanded] = useState(false);
  const reminderDaysAgo = getDaysFromNow(preview?.lastReminder?.sent_at);
  const hasRecentReminder = typeof reminderDaysAgo === 'number' && reminderDaysAgo <= 7;
  const hasReminder = !!preview?.lastReminder;
  const reminderDateLabel = hasReminder ? formatDateTime(preview.lastReminder.sent_at) : '-';
  const reminderChannelLabel = hasReminder ? getChannelLabel(preview.lastReminder.channel) : '-';
  const reminderChipLabel = hasReminder
    ? `Gia sollecitato ${formatDate(preview.lastReminder.sent_at)}`
    : null;

  return (
    <>
      <TableRow 
        hover 
        sx={{ 
          '& > *': { borderBottom: 'unset' },
          bgcolor: selected
            ? 'action.selected'
            : hasRecentReminder
              ? '#fff4e5'
              : 'inherit'
        }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            checked={selected}
            onChange={(e) => onSelect(preview.customerId, e.target.checked)}
          />
        </TableCell>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        {!isMobile && (
          <TableCell>
            <Typography variant="body2" fontWeight="medium">
              {preview.customerCode}
            </Typography>
          </TableCell>
        )}
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {preview.customerId ? (
              <Link
                component={RouterLink}
                to={`/crm/clienti/${preview.customerId}`}
                target="_blank"
                rel="noopener"
                underline="hover"
                variant="body2"
                sx={{ fontWeight: 500 }}
              >
                {preview.customerName}
              </Link>
            ) : (
              <Typography variant="body2">
                {preview.customerName}
              </Typography>
            )}
            {hasReminder && (
              <Tooltip title={`Ultimo invio: ${reminderDateLabel} via ${reminderChannelLabel}`}>
                <Chip
                  size="small"
                  label={reminderChipLabel}
                  color={hasRecentReminder ? 'warning' : 'default'}
                  variant={hasRecentReminder ? 'filled' : 'outlined'}
                />
              </Tooltip>
            )}
            {channel === 'whatsapp' && preview.hasInvioEmail && (
              <Tooltip title={`Ha anche invio email: ${preview.email}`}>
                <EmailIcon sx={{ fontSize: 16, color: '#1976d2', opacity: 0.7 }} />
              </Tooltip>
            )}
            {channel === 'whatsapp' && !preview.hasInvioEmail && (
              <Tooltip title="Invio tramite posta">
                <PostaIcon sx={{ fontSize: 16, color: '#ed6c02', opacity: 0.7 }} />
              </Tooltip>
            )}
            {channel === 'email' && preview.whatsappNumber && (
              <Tooltip title={`Ha anche WhatsApp: ${preview.whatsappNumber}`}>
                <WhatsAppIcon sx={{ fontSize: 16, color: '#25D366', opacity: 0.7 }} />
              </Tooltip>
            )}
            {channel === 'posta' && preview.whatsappNumber && (
              <Tooltip title={`Ha anche WhatsApp: ${preview.whatsappNumber}`}>
                <WhatsAppIcon sx={{ fontSize: 16, color: '#25D366', opacity: 0.7 }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        {!isMobile && (
          <TableCell>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {channel === 'email' ? preview.email : channel === 'whatsapp' ? preview.whatsappNumber : (preview.indirizzo || 'N/D')}
            </Typography>
          </TableCell>
        )}
        <TableCell align="center">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip 
              label={preview.invoiceCount} 
              size="small" 
              color="warning"
            />
            {(preview.reminderCountLast30d || 0) > 0 && (
              <Tooltip title={`Invii ultimi 30 giorni: ${preview.reminderCountLast30d}`}>
                <Chip
                  label={`${preview.reminderCountLast30d}x 30gg`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight="bold" color="error">
            {formatCurrency(preview.totalAmount)}
          </Typography>
        </TableCell>
        {type === 'disconnection' && !isMobile && (
          <TableCell align="center">
            <Chip 
              label={preview.deadlineDate} 
              size="small" 
              color="error"
              variant="outlined"
            />
          </TableCell>
        )}
      </TableRow>
      
      {/* Riga espansa con preview email */}
      <TableRow>
        <TableCell colSpan={type === 'disconnection' ? 9 : 8} sx={{ py: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 2 }}>
              {/* Dettaglio fatture */}
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                📋 Fatture incluse nel sollecito:
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>N° Fattura</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Scadenza</TableCell>
                      <TableCell align="right">Importo</TableCell>
                      <TableCell align="right">Pagato</TableCell>
                      <TableCell align="right">Residuo</TableCell>
                      <TableCell align="right">Mora</TableCell>
                      <TableCell align="center">GG Ritardo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.invoices.map((inv, idx) => (
                      <TableRow 
                        key={idx}
                        sx={inv.isScaduta === false ? { fontStyle: 'italic', color: 'text.secondary', bgcolor: 'grey.50' } : {}}
                      >
                        <TableCell sx={inv.isScaduta === false ? { color: 'text.secondary', fontStyle: 'italic' } : {}}>
                          <strong>{inv.numero}</strong>
                          {inv.isScaduta === false && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              non scaduta
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={inv.isScaduta === false ? { color: 'text.secondary' } : {}}>
                          {formatDate(inv.emissione)}
                        </TableCell>
                        <TableCell sx={inv.isScaduta === false ? { color: 'text.secondary' } : {}}>
                          {formatDate(inv.scadenza)}
                        </TableCell>
                        <TableCell align="right" sx={inv.isScaduta === false ? { color: 'text.secondary' } : {}}>
                          {formatCurrency(inv.totale)}
                        </TableCell>
                        <TableCell align="right" sx={inv.isScaduta === false ? { color: 'text.secondary' } : {}}>
                          {formatCurrency(inv.pagato)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', ...(inv.isScaduta === false ? { color: 'text.secondary' } : {}) }}>
                          {formatCurrency(inv.residuo)}
                        </TableCell>
                        <TableCell align="right" sx={inv.isScaduta === false ? { color: 'text.secondary' } : {}}>
                          {formatCurrency(inv.mora || 0)}
                        </TableCell>
                        <TableCell align="center">
                          {inv.isScaduta === false ? (
                            <Chip label="—" size="small" variant="outlined" />
                          ) : (
                            <Chip 
                              label={inv.giorniRitardo} 
                              size="small"
                              color={inv.giorniRitardo > 60 ? 'error' : inv.giorniRitardo > 30 ? 'warning' : 'default'}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Righe di riepilogo */}
                    {preview.totalResiduo != null && (
                      <>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold', borderTop: '2px solid #333' }}>
                            Subtotale residuo:
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', borderTop: '2px solid #333' }}>
                            {formatCurrency(preview.totalResiduo)}
                          </TableCell>
                          <TableCell colSpan={2} sx={{ borderTop: '2px solid #333' }} />
                        </TableRow>
                        {(preview.totalMora || 0) > 0 && (
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell colSpan={5} align="right">Interessi di mora:</TableCell>
                            <TableCell align="right">{formatCurrency(preview.totalMora)}</TableCell>
                            <TableCell colSpan={2} />
                          </TableRow>
                        )}
                        {(preview.speseSollecito || 0) > 0 && (
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell colSpan={5} align="right">Spese di sollecito:</TableCell>
                            <TableCell align="right">{formatCurrency(preview.speseSollecito)}</TableCell>
                            <TableCell colSpan={2} />
                          </TableRow>
                        )}
                        <TableRow sx={{ bgcolor: 'warning.light' }}>
                          <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                            TOTALE DA CORRISPONDERE:
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                            {formatCurrency(preview.totalAmount)}
                          </TableCell>
                          <TableCell colSpan={2} />
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Preview email/WA HTML */}
              {channel === 'email' && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    📧 Anteprima email:
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      maxHeight: 400, 
                      overflow: 'auto',
                      bgcolor: 'grey.50'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      <strong>Oggetto:</strong> {preview.subject}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <div 
                      dangerouslySetInnerHTML={{ __html: preview.bodyHtml }}
                      style={{ fontSize: '14px' }}
                    />
                  </Paper>
                </>
              )}
              {channel === 'whatsapp' && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    📱 Numero WhatsApp: <strong>{preview.whatsappNumber}</strong>
                  </Typography>
                </>
              )}
              {channel === 'posta' && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    📮 Indirizzo: <strong>{preview.indirizzo || 'Non disponibile'}</strong>
                  </Typography>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// Pannello Tab Promemoria o Solleciti
const ReminderPanel = ({ type, aziendaId, channel = 'email' }) => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [submissionResult, setSubmissionResult] = useState(null);
  useEffect(() => setSubmissionResult(null), [type, aziendaId, channel]);

  // Carica preview
  const { data: previewData, isLoading, error, refetch } = useQuery({
    queryKey: [type === 'reminder' ? 'reminders-preview' : 'disconnection-preview', aziendaId, channel],
    queryFn: async () => {
      const response = await invoicesApi.getRemindersPreview(type, { azienda_id: aziendaId, channel });
      return response.data;
    },
    staleTime: 60000, // 1 minuto
    enabled: !!aziendaId,
  });

  // Mutation per invio email
  const sendMutation = useMutation({
    mutationFn: async (customerIds) => {
      if (channel === 'whatsapp') {
        const response = await invoicesApi.sendRemindersWhatsApp(type, customerIds, { azienda_id: aziendaId });
        return response.data;
      }
      const response = await invoicesApi.sendReminders(type, customerIds, { azienda_id: aziendaId });
      return response.data;
    },
    onMutate: () => setSubmissionResult(null),
    onSuccess: (data) => {
      const channelLabel = channel === 'whatsapp' ? 'messaggi WhatsApp' : 'solleciti';
      const summary = data.data?.summary || {};
      const hasPendingConnection = data.data?.whatsapp_status !== 'ready' && (summary.queued || summary.alreadyQueued);
      const severity = channel !== 'whatsapp' ? 'success'
        : summary.failed && !summary.queued ? 'error'
        : summary.failed || summary.skipped || hasPendingConnection ? 'warning' : 'success';
      const feedback = {
        open: true,
        message: data.message || (summary.sent || 0) + ' ' + channelLabel + ' inviati con successo',
        severity
      };
      setSnackbar(feedback);
      if (channel === 'whatsapp') {
        const details = (data.data?.results || []).filter(r => r.message || r.error || r.warning).map(r => ({
          customerId: r.customerId,
          customerName: previewData?.data?.previews?.find(p => String(p.customerId) === String(r.customerId))?.customerName || 'Cliente ' + r.customerId,
          message: r.message || r.error || r.warning
        }));
        setSubmissionResult({ ...feedback, details });
      }
      setSelectedCustomers(new Set());
      setConfirmDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: [type === 'reminder' ? 'reminders-preview' : 'disconnection-preview'] });
      queryClient.invalidateQueries({ queryKey: ['reminders-history'] });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Errore nell\'invio',
        severity: 'error'
      });
      setConfirmDialogOpen(false);
    }
  });

  const handleSelectAll = useCallback((checked) => {
    if (checked && previewData?.data?.previews) {
      setSelectedCustomers(new Set(previewData.data.previews.map(p => p.customerId)));
    } else {
      setSelectedCustomers(new Set());
    }
  }, [previewData]);

  const handleSelect = useCallback((customerId, checked) => {
    setSelectedCustomers(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(customerId);
      } else {
        newSet.delete(customerId);
      }
      return newSet;
    });
  }, []);

  const handleSend = () => {
    if (channel === 'posta') {
      handleGeneratePdf();
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleGeneratePdf = async () => {
    try {
      const response = await invoicesApi.generateRemindersPdf(type, Array.from(selectedCustomers), { azienda_id: aziendaId });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `solleciti_posta_${Date.now()}.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'PDF generati e scaricati con successo', severity: 'success' });
      setSelectedCustomers(new Set());
      queryClient.invalidateQueries({ queryKey: [type === 'reminder' ? 'reminders-preview' : 'disconnection-preview'] });
      queryClient.invalidateQueries({ queryKey: ['reminders-history'] });
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Errore nella generazione PDF', severity: 'error' });
    }
  };

  const handleConfirmSend = () => {
    sendMutation.mutate(Array.from(selectedCustomers));
  };

  const previews = previewData?.data?.previews || [];
  const settings = previewData?.data?.settings || {};
  const allSelected = previews.length > 0 && selectedCustomers.size === previews.length;
  const someSelected = selectedCustomers.size > 0 && selectedCustomers.size < previews.length;

  // Calcola totali per i selezionati
  const selectedPreviews = previews.filter(p => selectedCustomers.has(p.customerId));
  const selectedTotal = selectedPreviews.reduce((sum, p) => sum + p.totalAmount, 0);
  const selectedInvoiceCount = selectedPreviews.reduce((sum, p) => sum + p.invoiceCount, 0);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Caricamento preview...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Errore nel caricamento: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      {channel === 'whatsapp' && submissionResult && (
        <Alert severity={submissionResult.severity} sx={{ mb: 2 }} onClose={() => setSubmissionResult(null)}>
          {submissionResult.message}
          {submissionResult.details.map((result, index) => (
            <Typography variant="body2" key={String(result.customerId) + '-' + index} sx={{ mt: 0.5 }}>
              <strong>{result.customerName}:</strong> {result.message}
            </Typography>
          ))}
        </Alert>
      )}
      {/* Header con statistiche */}
      <Box sx={{ mb: 2, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 1 : 2 }}>
        <Box>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
            {type === 'reminder' ? '📩 Promemoria Pagamento' : '⚠️ Solleciti con Intimazione Distacco'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Soglia: <strong>{settings.daysThreshold} giorni</strong> | 
            Clienti: <strong>{previews.length}</strong> | 
            {!isMobile && <>Fatture: <strong>{settings.totalInvoices}</strong> | </>}
            Totale: <strong>{formatCurrency(settings.totalAmount)}</strong>
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            {isMobile ? '' : 'Aggiorna'}
          </Button>
          
          <Button
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            color={type === 'reminder' ? 'primary' : 'error'}
            startIcon={sendMutation.isPending ? <CircularProgress size={20} color="inherit" /> : 
              channel === 'whatsapp' ? <WhatsAppIcon /> : channel === 'posta' ? <PdfIcon /> : <SendIcon />}
            disabled={selectedCustomers.size === 0 || sendMutation.isPending}
            onClick={handleSend}
            sx={channel === 'whatsapp' ? { bgcolor: '#25D366', '&:hover': { bgcolor: '#1da851' } } : {}}
          >
            {channel === 'whatsapp' ? `WhatsApp (${selectedCustomers.size})` : 
             channel === 'posta' ? `Genera PDF (${selectedCustomers.size})` : 
             `Invia Email (${selectedCustomers.size})`}
          </Button>
        </Box>
      </Box>

      {/* Info selezione */}
      {selectedCustomers.size > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Selezionati <strong>{selectedCustomers.size}</strong> clienti | 
          <strong>{selectedInvoiceCount}</strong> fatture | 
          Totale: <strong>{formatCurrency(selectedTotal)}</strong>
        </Alert>
      )}

      {/* Tabella preview */}
      {previews.length === 0 ? (
        <Alert severity="success" sx={{ m: 2 }}>
          {type === 'reminder' 
            ? `Nessun cliente con fatture scadute da più di ${settings.daysThreshold} giorni`
            : `Nessun cliente con fatture scadute da più di ${settings.daysThreshold} giorni da sollecitare`
          }
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: type === 'reminder' ? 'primary.light' : 'error.light' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                  />
                </TableCell>
                <TableCell sx={{ width: 50, color: 'white' }}></TableCell>
                {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Codice</TableCell>}
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cliente</TableCell>
                {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  {channel === 'email' ? 'Email' : channel === 'whatsapp' ? 'WhatsApp' : 'Indirizzo'}
                </TableCell>}
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>{isMobile ? 'Fatt.' : 'Fatture'}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Importo</TableCell>
                {type === 'disconnection' && !isMobile && (
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Termine</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {previews.map((preview) => (
                <ExpandableRow
                  key={preview.customerId}
                  preview={preview}
                  selected={selectedCustomers.has(preview.customerId)}
                  onSelect={handleSelect}
                  type={type === 'reminder' ? 'reminder' : 'disconnection'}
                  isMobile={isMobile}
                  channel={channel}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog conferma invio */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} fullScreen={isMobile}>
        <DialogTitle>
          {channel === 'whatsapp' 
            ? (type === 'reminder' ? '📱 Conferma Invio WhatsApp Promemoria' : '📱 Conferma Invio WhatsApp Solleciti')
            : (type === 'reminder' ? '📩 Conferma Invio Promemoria' : '⚠️ Conferma Invio Solleciti')}
        </DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Stai per inviare {selectedCustomers.size} {channel === 'whatsapp' ? 'messaggi WhatsApp' : 'email'} di {type === 'reminder' ? 'promemoria pagamento' : 'sollecito con intimazione distacco'}.
          </Typography>
          <Alert severity={type === 'reminder' ? 'info' : 'warning'} sx={{ mb: 2 }}>
            <strong>{selectedInvoiceCount}</strong> fatture per un totale di <strong>{formatCurrency(selectedTotal)}</strong>
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {channel === 'whatsapp' 
              ? 'I messaggi WhatsApp verranno accodati e inviati ai numeri dei clienti selezionati.'
              : 'Le email verranno inviate agli indirizzi dei clienti selezionati e registrate nello storico solleciti.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color={type === 'reminder' ? 'primary' : 'error'}
            onClick={handleConfirmSend}
            disabled={sendMutation.isPending}
            startIcon={sendMutation.isPending ? <CircularProgress size={20} /> : 
              channel === 'whatsapp' ? <WhatsAppIcon /> : <SendIcon />}
            sx={channel === 'whatsapp' ? { bgcolor: '#25D366', '&:hover': { bgcolor: '#1da851' } } : {}}
          >
            Conferma Invio
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Pannello Storico Solleciti
const HistoryPanel = ({ aziendaId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(25);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: historyData, isLoading } = useQuery({
    queryKey: ['reminders-history', page, rowsPerPage, aziendaId],
    queryFn: async () => {
      const response = await invoicesApi.getRemindersHistory({
        page: page + 1,
        limit: rowsPerPage,
        azienda_id: aziendaId
      });
      return response.data;
    },
    enabled: !!aziendaId,
  });

  const history = historyData?.data?.history || [];
  const pagination = historyData?.data?.pagination || {};

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        📜 Storico Solleciti Inviati
      </Typography>

      {history.length === 0 ? (
        <Alert severity="info">
          Nessun sollecito inviato finora.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.200' }}>
                <TableCell>Data Invio</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Cliente</TableCell>
                {!isMobile && <TableCell>Email</TableCell>}
                <TableCell align="center">Fatt.</TableCell>
                <TableCell align="right">Importo</TableCell>
                {!isMobile && <TableCell>Inviato da</TableCell>}
                <TableCell align="center">Stato</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    {format(new Date(item.sent_at), 'dd/MM/yyyy HH:mm', { locale: it })}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Chip
                        size="small"
                        label={item.reminder_type === 'payment_reminder' ? 'Promemoria' : 'Sollecito'}
                        color={item.reminder_type === 'payment_reminder' ? 'primary' : 'error'}
                        variant="outlined"
                      />
                      {item.channel === 'whatsapp' && (
                        <Chip size="small" icon={<WhatsAppIcon />} label="WA" sx={{ bgcolor: '#25D366', color: 'white' }} />
                      )}
                      {item.channel === 'posta' && (
                        <Chip size="small" icon={<PostaIcon />} label="Posta" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {item.customer?.ragione_sociale || 
                     `${item.customer?.cognome || ''} ${item.customer?.nome || ''}`.trim() ||
                     `#${item.customer_id}`}
                  </TableCell>
                  {!isMobile && (
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                      {item.email_sent_to}
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <Chip 
                      size="small" 
                      label={item.invoices_included?.length || '-'}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(item.total_amount)}
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {item.sent_by_user_name || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell align="center">
                    {item.status === 'sent' ? (
                      <Tooltip title="Inviato con successo">
                        <CheckIcon color="success" />
                      </Tooltip>
                    ) : item.status === 'failed' ? (
                      <Tooltip title={item.error_message || 'Invio fallito'}>
                        <ErrorIcon color="error" />
                      </Tooltip>
                    ) : (
                      <Chip size="small" label={item.status} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Paginazione semplice */}
      {pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
          <Button 
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
          >
            Precedente
          </Button>
          <Typography sx={{ alignSelf: 'center' }}>
            Pagina {page + 1} di {pagination.totalPages}
          </Typography>
          <Button 
            disabled={page >= pagination.totalPages - 1}
            onClick={() => setPage(p => p + 1)}
          >
            Successiva
          </Button>
        </Box>
      )}
    </Box>
  );
};

// Sub-tabs per canale di invio (Email, WhatsApp, Posta)
const ChannelSubTabs = ({ type, aziendaId }) => {
  const [channelTab, setChannelTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const channels = ['email', 'whatsapp', 'posta'];
  const channel = channels[channelTab];

  return (
    <Box>
      <Tabs
        value={channelTab}
        onChange={(e, v) => setChannelTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        variant={isMobile ? 'scrollable' : 'standard'}
        scrollButtons={isMobile ? 'auto' : false}
        allowScrollButtonsMobile
      >
        <Tab icon={<EmailIcon />} iconPosition="start" label="Email" sx={{ minHeight: 48 }} />
        <Tab icon={<WhatsAppIcon />} iconPosition="start" label="WhatsApp" 
          sx={{ minHeight: 48, '&.Mui-selected': { color: '#25D366' } }} />
        <Tab icon={<PostaIcon />} iconPosition="start" label="Posta" sx={{ minHeight: 48 }} />
      </Tabs>

      <ReminderPanel type={type} aziendaId={aziendaId} channel={channel} />
    </Box>
  );
};

// Componente principale
const RemindersPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [filterAzienda, setFilterAzienda] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Carica lista aziende
  const { data: aziendeData } = useQuery({
    queryKey: ['aziende'],
    queryFn: () => aziendeApi.getAll().then(res => res.data?.data || res.data || []),
    staleTime: 5 * 60 * 1000,
  });

  // Preselezione azienda default
  useEffect(() => {
    if (aziendeData && aziendeData.length > 0 && !filterAzienda) {
      const defaultAz = aziendeData.find(a => a.is_default);
      setFilterAzienda(defaultAz ? defaultAz.id : aziendeData[0].id);
    }
  }, [aziendeData, filterAzienda]);

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Card>
        <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h5'} component="h1" fontWeight="bold">
              Solleciti e Promemoria
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: isMobile ? 150 : 220 }}>
                <InputLabel id="azienda-select-label">Azienda</InputLabel>
                <Select
                  labelId="azienda-select-label"
                  value={filterAzienda}
                  onChange={(e) => setFilterAzienda(e.target.value)}
                  label="Azienda"
                  startAdornment={<BusinessIcon sx={{ mr: 0.5, color: 'action.active' }} />}
                >
                  {(aziendeData || []).map(az => (
                    <MenuItem key={az.id} value={az.id}>{az.company_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size={isMobile ? 'small' : 'medium'}
                startIcon={<SettingsIcon />}
                href="/settings?tab=reminders"
              >
                {isMobile ? 'Config.' : 'Configura'}
              </Button>
            </Box>
          </Box>

          <Tabs 
            value={tabValue} 
            onChange={(e, v) => setTabValue(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            allowScrollButtonsMobile
          >
            <Tab 
              icon={<EmailIcon />} 
              iconPosition="start" 
              label={isMobile ? 'Promemoria' : 'Promemoria (>10gg)'} 
            />
            <Tab 
              icon={<WarningIcon />} 
              iconPosition="start" 
              label={isMobile ? 'Solleciti' : 'Solleciti Distacco (>60gg)'} 
              sx={{ color: 'error.main' }}
            />
            <Tab 
              icon={<HistoryIcon />} 
              iconPosition="start" 
              label="Storico" 
            />
          </Tabs>

          {/* Tab Promemoria */}
          {tabValue === 0 && <ChannelSubTabs type="reminder" aziendaId={filterAzienda} />}

          {/* Tab Solleciti Distacco */}
          {tabValue === 1 && <ChannelSubTabs type="disconnection" aziendaId={filterAzienda} />}

          {/* Tab Storico */}
          {tabValue === 2 && <HistoryPanel aziendaId={filterAzienda} />}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RemindersPage;
